import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { AREA_GROUP, NAV_GROUPS, ROUTES } from "@/content/routes";
import { Container, Mono, Wordmark } from "@/components/site/primitives";
import { cn } from "@/lib/utils";
import { MobileMenu } from "./MobileMenu";

const NavPanel = ({ id, group }) => (
  <div id={id} className="absolute left-0 top-full w-[380px] pt-3" data-testid={`nav-panel-${group.key}`}>
    <div className="fade-up border border-o-line bg-o-surface p-2 shadow-2xl" style={{ animationDuration: "0.25s" }}>
      <p className="px-3 pb-2 pt-2"><Mono>{group.note}</Mono></p>
      <ul>
        {group.items.map((it) => (
          <li key={it.key}>
            <Link
              to={ROUTES[it.key].path}
              state={it.via ? { via: it.via } : undefined}
              data-testid={`nav-panel-${group.key}-${it.key}`}
              className="flex items-baseline justify-between gap-4 px-3 py-2.5 transition-colors hover:bg-o-raise"
            >
              <span className="text-sm font-medium text-o-text">{it.label}</span>
              {it.note && <span className="text-right text-xs text-o-dim">{it.note}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const NavGroup = ({ group, active, open, setOpen }) => {
  const panelId = `nav-panel-${group.key}-list`;
  return (
    <div className="relative" onMouseEnter={() => setOpen(group.key)}>
      <div className="flex items-center">
        <Link
          to={ROUTES[group.key].path}
          data-testid={`nav-${group.key}`}
          aria-current={active ? "true" : undefined}
          className={cn("relative px-3 py-2 text-sm transition-colors", active ? "text-o-text" : "text-o-dim hover:text-o-text")}
        >
          {group.label}
          {active && <span aria-hidden className="absolute inset-x-3 -bottom-0.5 h-px bg-o-accent" />}
        </Link>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`${group.label} pages`}
          onClick={() => setOpen(open ? null : group.key)}
          data-testid={`nav-${group.key}-toggle`}
          className="-ml-2 p-1.5 text-o-dim transition-colors hover:text-o-text"
        >
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
        </button>
      </div>
      {open && <NavPanel id={panelId} group={group} />}
    </div>
  );
};

export const SiteHeader = ({ currentKey, theme }) => {
  const [open, setOpen] = useState(null);
  const { pathname, state } = useLocation();
  const viaSoftware = state?.via === "software" && ["engine", "stewardship"].includes(currentKey);
  const activeGroup = viaSoftware ? "enterprise" : currentKey ? AREA_GROUP[ROUTES[currentKey].area] : null;
  useEffect(() => {
    setOpen(null);
  }, [pathname]);
  return (
    <header
      className="site-header sticky top-0 z-50 border-b border-o-line"
      data-testid="site-header"
      onMouseLeave={() => setOpen(null)}
      onKeyDown={(e) => e.key === "Escape" && setOpen(null)}
    >
      <Container className="flex h-16 items-center justify-between gap-6">
        <Wordmark testid="header-wordmark" />
        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex">
          {NAV_GROUPS.map((g) => (
            <NavGroup key={g.key} group={g} active={activeGroup === g.key} open={open === g.key} setOpen={setOpen} />
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/engage"
            data-testid="nav-engage-cta"
            className={cn(
              "hidden border px-4 py-2 text-sm font-medium transition-colors sm:inline-flex",
              currentKey === "engage" ? "border-o-accent bg-o-accent text-o-accent-ink" : "border-o-strong hover:border-o-accent hover:bg-o-soft"
            )}
          >
            Engage
          </Link>
          <MobileMenu theme={theme} />
        </div>
      </Container>
    </header>
  );
};
